import React from 'react';

import { GradeKpis, GradeWidgets } from './styles';
import KpiCard from './KpiCard';
import WidgetCard from './WidgetCard';
import TabelaDados, { TabelaDadosProps } from './TabelaDados';
import { BarrasHorizontais, BarrasVerticais, Linha, Rosca } from '../Graficos';
import { Formato, Serie, Widget, WidgetKpi } from '../../types/Dashboard';

function tabelaDeSeries(series: Serie[], formato: Formato): TabelaDadosProps {
  return {
    colunas: [
      { campo: 'rotulo', caption: 'Período', formato: 'texto' },
      ...series.map((s, i) => ({ campo: `s${i}`, caption: s.nome, formato }))
    ],
    linhas: (series[0]?.pontos ?? []).map((p, idx) => {
      const linha: Record<string, string | number> = { rotulo: p.rotulo };
      series.forEach((s, i) => (linha[`s${i}`] = s.pontos[idx]?.valor ?? 0));
      return linha;
    })
  };
}

// Séries no tempo e tabelas ocupam a linha inteira; rosca e ranking, meia linha no desktop
function ehLarguraTotal(widget: Widget) {
  return widget.tipo === 'linha' || widget.tipo === 'tabela' || (widget.tipo === 'barra' && !!widget.series?.length);
}

function GraficoWidget({ widget, sozinho }: { widget: Exclude<Widget, WidgetKpi>; sozinho: boolean }) {
  switch (widget.tipo) {
    case 'rosca':
      return (
        <WidgetCard titulo={widget.titulo} larguraTotal={sozinho}>
          <Rosca itens={widget.itens} formato={widget.formato} />
        </WidgetCard>
      );
    case 'barra':
      if (widget.series?.length) {
        return (
          <WidgetCard titulo={widget.titulo} tabela={tabelaDeSeries(widget.series, widget.formato)} larguraTotal>
            <BarrasVerticais series={widget.series} formato={widget.formato} />
          </WidgetCard>
        );
      }
      return (
        <WidgetCard titulo={widget.titulo} larguraTotal={sozinho}>
          <BarrasHorizontais itens={widget.itens ?? []} formato={widget.formato} />
        </WidgetCard>
      );
    case 'linha':
      return (
        <WidgetCard titulo={widget.titulo} tabela={tabelaDeSeries(widget.series, widget.formato)} larguraTotal>
          <Linha series={widget.series} formato={widget.formato} />
        </WidgetCard>
      );
    case 'tabela':
      return (
        <WidgetCard titulo={widget.titulo} larguraTotal>
          <TabelaDados colunas={widget.colunas} linhas={widget.linhas} />
        </WidgetCard>
      );
  }
}

// Monta o dashboard a partir da lista de widgets da API: KPIs seguidos viram uma grade;
// os gráficos seguem na ordem em que vieram, em 2 colunas no desktop.
export default function WidgetRenderer({ widgets }: { widgets: Widget[] }) {
  const blocos: (Widget | WidgetKpi[])[] = [];
  for (const w of widgets) {
    const ultimo = blocos[blocos.length - 1];
    if (w.tipo === 'kpi') {
      if (Array.isArray(ultimo)) ultimo.push(w);
      else blocos.push([w]);
    } else blocos.push(w);
  }

  // Num trecho com quantidade ímpar de widgets de meia linha, o último ocupa a linha toda
  // (senão sobra um buraco ao lado dele)
  const sozinhos = new Set<string>();
  let meias: Widget[] = [];
  for (const b of [...blocos, null]) {
    if (b && !Array.isArray(b) && !ehLarguraTotal(b)) {
      meias.push(b);
      continue;
    }
    if (meias.length % 2 === 1) sozinhos.add(meias[meias.length - 1].id);
    meias = [];
  }

  return (
    <GradeWidgets>
      {blocos.map((b, i) => (Array.isArray(b) ? (
        <GradeKpis key={`kpis${i}`}>
          {b.map(kpi => <KpiCard key={kpi.id} widget={kpi} />)}
        </GradeKpis>
      ) : (
        <GraficoWidget key={b.id} widget={b as Exclude<Widget, WidgetKpi>} sozinho={sozinhos.has(b.id)} />
      )))}
    </GradeWidgets>
  );
}
